import { useEffect, useState, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import api from '../utils/api'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import CollectionIntel from '../components/CollectionIntel'

interface SnapshotResult {
  id: string
  status: 'pending' | 'processing' | 'complete' | 'error'
  results: {
    broker_count: number
    breach_count: number
    risk_score: number
    vehicle_exposed: boolean | null
  } | null
}

// Animated number counter
function Counter({ target, duration = 1500, suffix = '' }: { target: number; duration?: number; suffix?: string }) {
  const [count, setCount] = useState(0)
  const startTime = useRef<number | null>(null)
  const rafRef = useRef<number>()

  useEffect(() => {
    startTime.current = null
    const animate = (timestamp: number) => {
      if (!startTime.current) startTime.current = timestamp
      const progress = Math.min((timestamp - startTime.current) / duration, 1)
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3)
      setCount(Math.round(eased * target))
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate)
      }
    }
    rafRef.current = requestAnimationFrame(animate)
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
  }, [target, duration])

  return <>{count.toLocaleString()}{suffix}</>
}

// Live scanning ticker — simulates source names being queried
const SCAN_SOURCES = [
  'Spokeo.com', 'BeenVerified.com', 'Whitepages.com', 'TruthFinder.com',
  'Intelius.com', 'FastPeopleSearch.com', 'PeopleFinder.com', 'Radaris.com',
  'MyLife.com', 'PeopleLooker.com', 'USSearch.com', 'PublicRecordsNow.com',
  'data.sec.gov (SEC EDGAR)', 'api.fec.gov (Campaign Finance)', 'FAA Aircraft Registry',
  'USCG Vessel Documentation', 'CourtListener (PACER)', 'HIBP Breach Database',
  'Dark Web Markets ×14', 'LinkedIn / Social Graph', 'OFAC Sanctions List',
  'County Property Records', 'UCC Commercial Filings', 'Professional License Boards',
  'LPR/ALPR Vehicle Records', 'PeopleSmart.com', 'Instant Checkmate', 'ZabaSearch.com',
  'AnyWho.com', 'Pipl Graph', 'FullContact API', 'LexisNexis (Public)',
]

function ScanTicker() {
  const [visibleSources, setVisibleSources] = useState<string[]>([])
  const indexRef = useRef(0)

  useEffect(() => {
    const shuffled = [...SCAN_SOURCES].sort(() => Math.random() - 0.5)
    const interval = setInterval(() => {
      if (indexRef.current < shuffled.length) {
        setVisibleSources(prev => [shuffled[indexRef.current], ...prev].slice(0, 5))
        indexRef.current++
      }
    }, 800)
    return () => clearInterval(interval)
  }, [])

  return (
    <div className="font-mono text-xs space-y-1 h-[108px] overflow-hidden">
      <AnimatePresence mode="popLayout">
        {visibleSources.map((src, i) => (
          <motion.div
            key={src}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1 - i * 0.15, x: 0 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-2"
            style={{ opacity: Math.max(0.2, 1 - i * 0.2) }}
          >
            <span className="text-green-400">›</span>
            <span className="text-dark-300">QUERYING</span>
            <span className="text-ginger-400">{src}</span>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}

// Big animated scanning ring
function ScanRing({ progress }: { progress: number }) {
  const radius = 80
  const circumference = 2 * Math.PI * radius
  const strokeDash = circumference * (progress / 100)

  return (
    <div className="relative w-48 h-48 mx-auto">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 200 200">
        {/* Background ring */}
        <circle cx="100" cy="100" r={radius} fill="none" stroke="#1a1a2e" strokeWidth="6" />
        {/* Progress ring */}
        <circle
          cx="100" cy="100" r={radius}
          fill="none"
          stroke="#ec7a1c"
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={`${strokeDash} ${circumference}`}
          style={{ transition: 'stroke-dasharray 0.3s ease' }}
        />
        {/* Glow effect */}
        <circle
          cx="100" cy="100" r={radius}
          fill="none"
          stroke="#ec7a1c"
          strokeWidth="1"
          strokeOpacity="0.3"
          strokeDasharray={`${strokeDash} ${circumference}`}
          filter="url(#glow)"
          style={{ transition: 'stroke-dasharray 0.3s ease' }}
        />
        <defs>
          <filter id="glow">
            <feGaussianBlur stdDeviation="4" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
      </svg>
      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-3xl font-bold text-ginger-400">{Math.round(progress)}%</div>
        <div className="text-xs text-dark-500 uppercase tracking-widest mt-1">Scanned</div>
      </div>
    </div>
  )
}

// Scanning phase component
function ScanningView({ id }: { id: string }) {
  const [elapsed, setElapsed] = useState(0)
  const [sourceCount, setSourceCount] = useState(0)
  const [classText, setClassText] = useState('INITIALIZING')

  const TOTAL_DURATION = 33000 // ms

  const phases = [
    { at: 0,     label: 'INITIALIZING COLLECTION MATRIX' },
    { at: 3000,  label: 'QUERYING DATA BROKER NETWORK' },
    { at: 10000, label: 'SCANNING BREACH DATABASES' },
    { at: 16000, label: 'ACCESSING PUBLIC RECORDS' },
    { at: 22000, label: 'DARK WEB INTELLIGENCE SWEEP' },
    { at: 27000, label: 'GOVERNMENT REGISTRY CROSS-CHECK' },
    { at: 31000, label: 'COMPUTING THREAT ASSESSMENT' },
  ]

  useEffect(() => {
    const start = Date.now()
    const interval = setInterval(() => {
      const e = Date.now() - start
      setElapsed(e)

      // Source count climbs to ~800 over the scan
      const pct = Math.min(e / TOTAL_DURATION, 1)
      setSourceCount(Math.round(pct * pct * 800))

      // Update phase label
      let currentPhase = phases[0].label
      for (const phase of phases) {
        if (e >= phase.at) currentPhase = phase.label
      }
      setClassText(currentPhase)
    }, 100)
    return () => clearInterval(interval)
  }, [])

  const progress = Math.min((elapsed / TOTAL_DURATION) * 98, 98)

  return (
    <div className="min-h-screen bg-dark-950" style={{ background: 'radial-gradient(ellipse at top, #0f0a00 0%, #080810 60%)' }}>
      <Navbar />

      <div className="pt-20 pb-12 px-4">
        <div className="max-w-4xl mx-auto">

          {/* Classification Banner */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center gap-3 mb-8 mt-4"
          >
            <div className="flex items-center gap-2 bg-red-950/60 border border-red-800/50 rounded px-4 py-1.5">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-red-400 text-xs font-bold tracking-[0.2em] uppercase">
                COLLECTION IN PROGRESS
              </span>
            </div>
          </motion.div>

          {/* Main Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-center mb-10"
          >
            <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-2">
              Intelligence Collection
              <span className="text-ginger-500"> Underway</span>
            </h1>
            <p className="text-dark-400 text-sm font-mono uppercase tracking-widest">
              Gingerbread! Executive Privacy &mdash; ICD-203 Protocol
            </p>
          </motion.div>

          {/* 3-column stat bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-3 gap-px bg-dark-700/50 rounded-xl overflow-hidden mb-8 border border-dark-700/50"
          >
            <div className="bg-dark-900 px-6 py-5 text-center">
              <div className="text-3xl font-black text-ginger-400 tabular-nums">{sourceCount.toLocaleString()}</div>
              <div className="text-xs text-dark-500 uppercase tracking-widest mt-1">Sources Queried</div>
            </div>
            <div className="bg-dark-900 px-6 py-5 text-center border-x border-dark-700/50">
              <div className="text-3xl font-black text-dark-100 tabular-nums">{Math.round(progress)}%</div>
              <div className="text-xs text-dark-500 uppercase tracking-widest mt-1">Complete</div>
            </div>
            <div className="bg-dark-900 px-6 py-5 text-center">
              <div className="text-3xl font-black text-green-400">8</div>
              <div className="text-xs text-dark-500 uppercase tracking-widest mt-1">PIRs Active</div>
            </div>
          </motion.div>

          {/* Main scanning visual */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">

            {/* Left: Ring + Phase */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-dark-900 border border-dark-700/50 rounded-xl p-6 flex flex-col items-center gap-6"
            >
              <ScanRing progress={progress} />

              {/* Phase label */}
              <div className="text-center">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={classText}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    className="text-ginger-400 font-mono font-bold text-xs tracking-widest uppercase"
                  >
                    {classText}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Overall bar */}
              <div className="w-full">
                <div className="w-full h-1.5 bg-dark-800 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full rounded-full"
                    style={{
                      width: `${progress}%`,
                      background: 'linear-gradient(90deg, #c85a00, #ec7a1c, #f09440)',
                    }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>
            </motion.div>

            {/* Right: Live ticker */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-dark-900 border border-dark-700/50 rounded-xl p-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                <span className="text-xs text-dark-400 uppercase tracking-widest font-mono">
                  Live Collection Feed
                </span>
              </div>

              <ScanTicker />

              <div className="mt-4 pt-4 border-t border-dark-800">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'OSINT', color: 'text-sky-400', count: Math.round(sourceCount * 0.6) },
                    { label: 'TECHINT', color: 'text-purple-400', count: Math.round(sourceCount * 0.25) },
                    { label: 'GOV RECORDS', color: 'text-blue-400', count: Math.round(sourceCount * 0.1) },
                    { label: 'DARK WEB', color: 'text-red-400', count: Math.round(sourceCount * 0.05) },
                  ].map(({ label, color, count }) => (
                    <div key={label} className="flex items-center justify-between bg-dark-800/50 rounded px-3 py-2">
                      <span className={`text-xs font-mono font-bold ${color}`}>{label}</span>
                      <span className="text-xs text-dark-400 tabular-nums">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* CollectionIntel live feed */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-dark-900 border border-dark-700/50 rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-ginger-500 animate-pulse" />
                <h3 className="text-sm font-bold text-dark-200 uppercase tracking-widest">
                  Intelligence Source Matrix
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-dark-600 border border-dark-700 rounded px-2 py-0.5">ICD-203</span>
                <span className="text-xs font-mono text-dark-600 border border-dark-700 rounded px-2 py-0.5">NATO STANAG 2511</span>
              </div>
            </div>
            <CollectionIntel scanId={id} compact />
          </motion.div>

          {/* Bottom disclaimer */}
          <div className="mt-6 text-center">
            <p className="text-dark-600 text-xs font-mono">
              GINGERBREAD! EXECUTIVE PRIVACY · MONADNOCK CYBER · EYES ONLY
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

// Results view
function ResultsView({ results }: { results: NonNullable<SnapshotResult['results']> }) {
  const severity =
    results.risk_score >= 80 ? 'CRITICAL' :
    results.risk_score >= 60 ? 'SEVERE' :
    results.risk_score >= 40 ? 'ELEVATED' : 'MODERATE'

  const severityColor =
    results.risk_score >= 80 ? 'text-red-400' :
    results.risk_score >= 60 ? 'text-orange-400' :
    results.risk_score >= 40 ? 'text-yellow-400' : 'text-green-400'

  const severityBg =
    results.risk_score >= 80 ? 'bg-red-500/10 border-red-500/40' :
    results.risk_score >= 60 ? 'bg-orange-500/10 border-orange-500/40' :
    results.risk_score >= 40 ? 'bg-yellow-500/10 border-yellow-500/40' : 'bg-green-500/10 border-green-500/40'

  const severityGlow =
    results.risk_score >= 80 ? 'shadow-red-500/20' :
    results.risk_score >= 60 ? 'shadow-orange-500/20' :
    results.risk_score >= 40 ? 'shadow-yellow-500/20' : 'shadow-green-500/20'

  return (
    <div className="min-h-screen" style={{ background: 'radial-gradient(ellipse at top, #100800 0%, #080810 60%)' }}>
      <Navbar />

      <div className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto">

          {/* Classification banner */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center gap-3 mb-8 mt-4"
          >
            <div className={`flex items-center gap-2 border rounded px-4 py-1.5 ${severityBg}`}>
              <div className={`w-2 h-2 rounded-full animate-pulse ${
                results.risk_score >= 60 ? 'bg-red-500' : results.risk_score >= 40 ? 'bg-yellow-500' : 'bg-green-500'
              }`} />
              <span className={`text-xs font-black tracking-[0.2em] uppercase ${severityColor}`}>
                THREAT ASSESSMENT COMPLETE — {severity} EXPOSURE
              </span>
            </div>
          </motion.div>

          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-center mb-10"
          >
            <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-2">
              Your Digital Exposure
              <span className="text-ginger-500"> Report</span>
            </h1>
            <p className="text-dark-400 text-sm">
              Gingerbread! Executive Privacy — Full-Spectrum OSINT Assessment
            </p>
          </motion.div>

          {/* Big risk score */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 20 }}
            className={`mb-8 rounded-2xl border p-8 text-center ${severityBg} shadow-2xl ${severityGlow}`}
          >
            <div className="text-xs font-mono text-dark-500 uppercase tracking-widest mb-4">
              Composite Risk Score · 0–100 Scale
            </div>
            <div className={`text-9xl font-black tabular-nums leading-none ${severityColor}`}
              style={{ textShadow: results.risk_score >= 60 ? '0 0 60px rgba(239,68,68,0.4)' : undefined }}>
              <Counter target={results.risk_score} duration={2000} />
            </div>
            <div className={`text-2xl font-bold mt-4 ${severityColor}`}>{severity} EXPOSURE</div>
            <p className="text-dark-400 text-sm mt-3 max-w-md mx-auto">
              {results.risk_score >= 80
                ? 'A motivated adversary has enough to act. Immediate remediation required.'
                : results.risk_score >= 60
                ? 'Significant exposure across multiple vectors. High-priority remediation advised.'
                : results.risk_score >= 40
                ? 'Meaningful exposure present. Remediation will materially reduce your risk.'
                : 'Lower exposure detected. Targeted cleanup recommended.'}
            </p>
          </motion.div>

          {/* Stats grid */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            {[
              {
                value: results.broker_count,
                label: 'Data Brokers',
                sublabel: 'with your personal information',
                color: 'text-ginger-400',
                icon: '🗂',
                alert: results.broker_count > 20,
              },
              {
                value: results.breach_count,
                label: 'Data Breaches',
                sublabel: 'exposing your credentials',
                color: 'text-red-400',
                icon: '🔓',
                alert: results.breach_count > 3,
              },
              {
                value: results.vehicle_exposed === null ? null : (results.vehicle_exposed ? 1 : 0),
                label: 'Vehicle / DMV',
                sublabel: results.vehicle_exposed === null ? 'status unknown' :
                          results.vehicle_exposed ? 'vehicle data exposed' : 'no exposure found',
                color: results.vehicle_exposed ? 'text-orange-400' : 'text-green-400',
                icon: results.vehicle_exposed ? '🚗' : '✓',
                alert: results.vehicle_exposed === true,
                custom: results.vehicle_exposed === null ? '?' : results.vehicle_exposed ? 'EXPOSED' : 'CLEAR',
              },
            ].map(({ value, label, sublabel, color, icon, alert, custom }) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className={`bg-dark-900 border rounded-xl p-5 text-center relative overflow-hidden ${
                  alert ? 'border-red-500/40' : 'border-dark-700/50'
                }`}
              >
                {alert && (
                  <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                )}
                <div className="text-2xl mb-2">{icon}</div>
                <div className={`text-4xl font-black ${color} tabular-nums`}>
                  {custom || (value !== null ? <Counter target={value} duration={1800} /> : '?')}
                </div>
                <div className="text-dark-300 text-sm font-semibold mt-2">{label}</div>
                <div className="text-dark-500 text-xs mt-1">{sublabel}</div>
              </motion.div>
            ))}
          </div>

          {/* PIR Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-dark-900 border border-dark-700/50 rounded-xl p-6 mb-8"
          >
            <div className="flex items-center gap-2 mb-5">
              <span className="text-xs font-mono font-bold text-ginger-500 border border-ginger-500/40 rounded px-2 py-0.5">
                PIR ASSESSMENT
              </span>
              <span className="text-dark-400 text-xs">Priority Intelligence Requirements — Free Scan Results</span>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              {[
                { pir: 'PIR-001', name: 'Cyber / Credential Threat', level: results.breach_count > 3 ? 'HIGH' : results.breach_count > 0 ? 'MODERATE' : 'LOW', color: results.breach_count > 3 ? 'text-red-400 border-red-500/30 bg-red-950/30' : 'text-yellow-400 border-yellow-500/30 bg-yellow-950/30' },
                { pir: 'PIR-002', name: 'Physical / Location Threat', level: results.broker_count > 20 ? 'HIGH' : 'MODERATE', color: results.broker_count > 20 ? 'text-red-400 border-red-500/30 bg-red-950/30' : 'text-yellow-400 border-yellow-500/30 bg-yellow-950/30' },
                { pir: 'PIR-003', name: 'Family Exposure', level: results.broker_count > 15 ? 'ELEVATED' : 'MODERATE', color: 'text-orange-400 border-orange-500/30 bg-orange-950/30' },
                { pir: 'PIR-004', name: 'Reputational Threat', level: 'FULL REPORT REQUIRED', color: 'text-dark-500 border-dark-700 bg-dark-800/50' },
                { pir: 'PIR-005', name: 'Financial Exposure', level: 'FULL REPORT REQUIRED', color: 'text-dark-500 border-dark-700 bg-dark-800/50' },
                { pir: 'PIR-006', name: 'Adversarial Collection', level: results.risk_score > 60 ? 'HIGH' : 'MODERATE', color: results.risk_score > 60 ? 'text-red-400 border-red-500/30 bg-red-950/30' : 'text-yellow-400 border-yellow-500/30 bg-yellow-950/30' },
              ].map(({ pir, name, level, color }) => (
                <div key={pir} className={`flex items-center justify-between border rounded-lg px-4 py-3 ${color}`}>
                  <div>
                    <span className="text-xs font-mono opacity-60">{pir} · </span>
                    <span className="text-sm font-semibold">{name}</span>
                  </div>
                  <div className="text-xs font-bold uppercase tracking-wide ml-4 flex-shrink-0">
                    {level}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Free opt-out — top 5 brokers */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className="bg-dark-900 border border-dark-700/50 rounded-xl p-6 mb-8"
          >
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs font-mono font-bold text-green-400 border border-green-500/40 rounded px-2 py-0.5">
                FREE
              </span>
              <span className="text-dark-300 text-sm font-semibold">Start Opting Out Now</span>
            </div>
            <p className="text-dark-400 text-sm mb-4">
              Here are 5 of the {results.broker_count} brokers selling your data. You can remove yourself right now:
            </p>

            <div className="space-y-2 mb-4">
              {[
                { name: 'Spokeo', url: 'https://www.spokeo.com/optout' },
                { name: 'BeenVerified', url: 'https://www.beenverified.com/app/optout/search' },
                { name: 'Whitepages', url: 'https://www.whitepages.com/suppression-requests' },
                { name: 'FastPeopleSearch', url: 'https://www.fastpeoplesearch.com/removal' },
                { name: 'TruthFinder', url: 'https://www.truthfinder.com/opt-out/' },
              ].map(broker => (
                <a
                  key={broker.name}
                  href={broker.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between bg-dark-800/50 border border-dark-700/50 rounded-lg px-4 py-3 hover:border-green-500/40 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                    <span className="text-sm text-dark-200 font-semibold">{broker.name}</span>
                    <span className="text-xs text-dark-500">listing found</span>
                  </div>
                  <span className="text-xs text-green-400 font-semibold group-hover:underline">
                    Opt Out →
                  </span>
                </a>
              ))}
            </div>

            <div className="flex items-center gap-2 pt-3 border-t border-dark-800">
              <div className="w-2 h-2 rounded-full bg-dark-600" />
              <span className="text-dark-500 text-xs">
                + {Math.max(results.broker_count - 5, 0)} more brokers with your data
              </span>
            </div>
          </motion.div>

          {/* $295 CTA — automated removal */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="rounded-2xl border border-ginger-500/40 bg-gradient-to-br from-ginger-950/40 to-dark-900 p-8"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-ginger-500/20 border border-ginger-500/30 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-ginger-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-black mb-1">
                  You just opted out of 5. We'll handle the other {Math.max(results.broker_count - 5, 0)}.
                </h2>
                <p className="text-dark-400 text-sm mb-5">
                  For $295, we automatically submit opt-out requests to every broker, file Google search removal requests
                  for your personal info, and send CCPA/GDPR right-to-delete notices to every company breached with your data.
                </p>

                <div className="grid md:grid-cols-2 gap-3 mb-6">
                  {[
                    `Opt-out requests to all ${results.broker_count} brokers`,
                    'Google personal info removal requests',
                    'CCPA/GDPR deletion notices to breached companies',
                    'Full intelligence report with all findings',
                    'Removal status dashboard — track every request',
                    'Breach details & compromised credentials',
                    '1,008+ source collection matrix',
                    'All 8 PIRs fully answered',
                  ].map(item => (
                    <div key={item} className="flex items-start gap-2 text-sm text-dark-300">
                      <svg className="w-4 h-4 text-ginger-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                      {item}
                    </div>
                  ))}
                </div>

                <div className="flex gap-3 flex-wrap items-center">
                  <Link to="/checkout/quick" className="btn btn-primary text-base px-6 py-3">
                    Remove My Data — $295
                  </Link>
                  <Link to="/checkout/full" className="btn btn-secondary">
                    Executive Intelligence Report — $2,500
                  </Link>
                </div>

                <p className="text-dark-600 text-xs mt-4">
                  Need court record sealing, social media takedowns, or ongoing monitoring?
                  Our partner <strong className="text-dark-500">360Privacy</strong> handles what automation can't.
                  We'll connect you after your report.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Sample report teaser */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-6 text-center"
          >
            <Link
              to="/sample-report"
              className="inline-flex items-center gap-2 text-sm text-dark-400 hover:text-ginger-400 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              See what a $2,500 Executive Report looks like →
            </Link>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default function Snapshot() {
  const { id } = useParams()
  const [snapshot, setSnapshot] = useState<SnapshotResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!id) return

    async function fetchSnapshot() {
      try {
        const response = await api.get(`/snapshot/${id}`)
        setSnapshot(response.data)

        if (response.data.status === 'pending' || response.data.status === 'processing') {
          setTimeout(fetchSnapshot, 2000)
        }
      } catch (err: any) {
        setError('Could not load results. The link may have expired.')
      } finally {
        setLoading(false)
      }
    }

    fetchSnapshot()
  }, [id])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#080810' }}>
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-ginger-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-dark-500 font-mono text-sm">CONNECTING TO COLLECTION PLATFORM...</p>
        </div>
      </div>
    )
  }

  if (error || !snapshot) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="pt-24 flex items-center justify-center min-h-screen">
          <div className="card max-w-md text-center">
            <div className="text-4xl mb-4">⚠</div>
            <h1 className="text-xl font-bold mb-2">Collection Error</h1>
            <p className="text-dark-400 mb-4">{error}</p>
            <Link to="/" className="btn btn-primary">Try Again</Link>
          </div>
        </div>
      </div>
    )
  }

  if (snapshot.status === 'pending' || snapshot.status === 'processing') {
    return <ScanningView id={id!} />
  }

  if (snapshot.status === 'error') {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="pt-24 flex items-center justify-center min-h-screen">
          <div className="card max-w-md text-center">
            <div className="text-4xl mb-4">⚠</div>
            <h1 className="text-xl font-bold mb-2">Scan Error</h1>
            <p className="text-dark-400 mb-4">An error occurred during collection. Please try again.</p>
            <Link to="/" className="btn btn-primary">Try Again</Link>
          </div>
        </div>
      </div>
    )
  }

  return <ResultsView results={snapshot.results!} />
}
